import { useQuery } from '@tanstack/react-query'
import { analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  toTransferResult,
  type AnalyticsTransfersPage,
} from '@/api/map/transfers'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

const PAGE_SIZE = 100
const MAX_PAGES = 50

function rangeParams(range?: { from: string; to: string }): URLSearchParams {
  const qs = new URLSearchParams()
  if (range?.from) qs.set('from', range.from.slice(0, 10))
  if (range?.to) qs.set('to', range.to.slice(0, 10))
  return qs
}

function emptyTransfers(query: api.TransferQuery): api.TransferResult {
  return {
    rows: [],
    total: 0,
    page: query.page ?? 1,
    pageSize: query.pageSize ?? 25,
    totals: { given: 0, taken: 0, net: 0, count: 0 },
    daily: [],
    balanceAfterIsLive: true,
  }
}

async function fetchAllTransferPages(
  agentId: string,
  range: { from: string; to: string } | undefined,
  direction?: 'give' | 'take',
): Promise<AnalyticsTransfersPage[]> {
  const pages: AnalyticsTransfersPage[] = []
  for (let page = 0; page < MAX_PAGES; page++) {
    const qs = rangeParams(range)
    qs.set('page', String(page))
    qs.set('size', String(PAGE_SIZE))
    if (direction) qs.set('direction', direction)
    const live = await analyticsFetch<AnalyticsTransfersPage>(
      `/v1/agents/${agentId}/transfers?${qs}`,
    )
    pages.push(live)
    const got = live.rows?.length ?? 0
    const total = live.total
    if (got < PAGE_SIZE) break
    if (typeof total === 'number' && (page + 1) * PAGE_SIZE >= total) break
  }
  return pages
}

async function loadTransfers(query: api.TransferQuery): Promise<api.TransferResult> {
  if (hasLiveAnalytics()) {
    const id = query.accountId ?? getCallerAgentId()
    if (!id) return emptyTransfers(query)
    try {
      const pages = await fetchAllTransferPages(id, query.range, query.direction)
      const mapped = toTransferResult(pages, id)

      const needle = query.search?.trim().toLowerCase() ?? ''
      const filtered = needle
        ? mapped.rows.filter((t) => {
            const from = t.from?.username ?? ''
            const to = t.to?.username ?? ''
            return (
              from.toLowerCase().includes(needle) ||
              to.toLowerCase().includes(needle) ||
              t.ref.toLowerCase().includes(needle) ||
              t.remark.toLowerCase().includes(needle)
            )
          })
        : mapped.rows

      const totals = needle
        ? filtered.reduce(
            (acc, t) => {
              if (t.fromId === id) acc.given += t.amount
              else if (t.toId === id) acc.taken += t.amount
              acc.count += 1
              return acc
            },
            { given: 0, taken: 0, net: 0, count: 0 },
          )
        : mapped.totals
      if (needle) {
        totals.given = Math.round(totals.given * 100) / 100
        totals.taken = Math.round(totals.taken * 100) / 100
        totals.net = Math.round((totals.taken - totals.given) * 100) / 100
      }

      const page = query.page ?? 1
      const pageSize = query.pageSize ?? 25
      const start = (page - 1) * pageSize

      return {
        rows: filtered.slice(start, start + pageSize),
        total: filtered.length,
        page,
        pageSize,
        totals,
        daily: mapped.daily,
        asOf: mapped.asOf,
        martStale: mapped.martStale,
        balanceAfterIsLive: true,
      }
    } catch (err) {
      if (isUnreachableApiError(err)) return emptyTransfers(query)
      throw err
    }
  }

  return api.listTransfers(query)
}

export function useTransfers(query: api.TransferQuery) {
  return useQuery({
    queryKey: keys.transfers(query),
    queryFn: () => loadTransfers(query),
    placeholderData: (previous) => previous,
  })
}
